import { Component, type ReactNode, type ErrorInfo } from 'react';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Loading chunk \d+ failed/i,
  /ChunkLoadError/i,
];

const RELOAD_FLAG_KEY = 'xc:chunk-reload-attempted';
const RELOAD_COOLDOWN_MS = 10_000;

function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false;
  const message = error.message || '';
  const name = error.name || '';
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message) || re.test(name));
}

function recentlyReloaded(): boolean {
  try {
    const raw = sessionStorage.getItem(RELOAD_FLAG_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    return Number.isFinite(ts) && Date.now() - ts < RELOAD_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function markReloadAttempt(): void {
  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, String(Date.now()));
  } catch {
    // storage may be unavailable (private mode, quota) — fall through
  }
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });

    if (isChunkLoadError(error) && !recentlyReloaded()) {
      markReloadAttempt();
      window.location.reload();
    }
  }

  handleReset = () => {
    if (isChunkLoadError(this.state.error)) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-8">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-red-800 dark:text-red-200 mb-2">Something went wrong</h2>
          <p className="text-red-600 dark:text-red-400 text-center mb-4 max-w-md">
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
          {import.meta.env.DEV && this.state.errorInfo && (
            <details className="mb-4 max-w-2xl w-full">
              <summary className="cursor-pointer text-sm text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300">
                View error details
              </summary>
              <pre className="mt-2 overflow-auto rounded bg-red-100 dark:bg-red-900/30 p-4 text-xs text-red-800 dark:text-red-200">
                {this.state.error?.stack}
                {'\n\nComponent Stack:\n'}
                {this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
          <button
            onClick={this.handleReset}
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

